/* ==========================================================================
   Drawer — one primitive, used for the cart and the mobile menu.
   --------------------------------------------------------------------------
   Accessibility is the whole job here. A drawer that opens is easy; one that
   traps focus, restores it on close, and hides the rest of the page from a
   screen reader is the part people skip.

   Cart contents are re-rendered by Shopify's Section Rendering API rather
   than rebuilt in JS, so money formatting, discounts and line properties
   stay in Liquid where they belong.
   ========================================================================== */

(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var openDrawer = null;
  var lastFocus = null;
  var scrollY = 0;

  function lockScroll() {
    scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.width = '100%';
  }

  function unlockScroll() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }

  /* Every control that opens a drawer must announce whether it is open, or a
     screen-reader user hears the same label in both states. */
  function setExpanded(el, state) {
    if (!el || !el.id) return;
    document
      .querySelectorAll('[data-drawer-open="' + el.id + '"]')
      .forEach(function (t) { t.setAttribute('aria-expanded', state ? 'true' : 'false'); });
  }

  function open(el, opener) {
    if (openDrawer) close(openDrawer);
    /* Prefer the element that triggered the open. activeElement is wrong when
       a drawer is opened programmatically — a scripted click does not focus. */
    lastFocus = opener || document.activeElement;
    el.hidden = false;
    /* Forced reflow rather than requestAnimationFrame: rAF is throttled in
       background tabs, and a drawer that opens without its class stays
       translated off-screen — visibly broken with no error. */
    void el.offsetWidth;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    lockScroll();
    openDrawer = el;

    var first = el.querySelector('[data-drawer-initial]') || el.querySelector(FOCUSABLE);
    if (first) first.focus();

    /* Never hide an ancestor of the drawer itself. The menu drawer is rendered
       inside the header section, which sits inside [data-drawer-hide]; marking
       that subtree aria-hidden would take the open menu — and the keyboard
       focus now inside it — out of the accessibility tree entirely. */
    document.querySelectorAll('[data-drawer-hide]').forEach(function (n) {
      if (n.contains(el)) return;
      n.setAttribute('aria-hidden', 'true');
    });

    setExpanded(el, true);
    /* A hook for content that needs to reset itself each time the drawer
       opens — the mobile nav's drill-down screens, for one, so the menu
       never reopens deep in a category from last time. */
    el.dispatchEvent(new CustomEvent('ef:drawer:opened'));
  }

  function close(el) {
    el = el || openDrawer;
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    unlockScroll();
    openDrawer = null;

    document.querySelectorAll('[data-drawer-hide]').forEach(function (n) {
      n.removeAttribute('aria-hidden');
    });

    setExpanded(el, false);

    /* Restore `hidden` when the slide-out finishes — with a timer fallback,
       because transitionend does not fire if the transition never starts
       (reduced motion, a hidden tab, or a zero duration). Without the
       fallback the drawer stays in the accessibility tree forever. */
    var settled = false;
    var done = function () {
      if (settled) return;
      settled = true;
      el.hidden = true;
      el.removeEventListener('transitionend', done);
    };
    el.addEventListener('transitionend', done);
    var ms = parseFloat(getComputedStyle(el).getPropertyValue('--eft-dur')) || 400;
    setTimeout(done, ms + 80);

    if (lastFocus && document.contains(lastFocus) && lastFocus !== document.body) lastFocus.focus();
  }

  /* Focus trap. Cheaper and more reliable than inert polyfills. */
  document.addEventListener('keydown', function (e) {
    if (!openDrawer) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;

    var items = Array.prototype.filter.call(
      openDrawer.querySelectorAll(FOCUSABLE),
      function (n) { return n.offsetParent !== null; }
    );
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) { e.preventDefault(); open(target, opener); }
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.hasAttribute('data-drawer-backdrop')) {
      e.preventDefault();
      close();
    }
  });

  /* ── Cart ─────────────────────────────────────────────────────────────── */

  function refreshCart() {
    return fetch(window.Shopify.routes.root + '?sections=cart-drawer')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data['cart-drawer'];
        if (!html) return;
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-cart-contents]');
        var current = document.querySelector('[data-cart-contents]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      });
  }

  /* Quantity changes and removals inside the drawer. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-change]');
    if (!btn) return;
    e.preventDefault();
    var line = btn.getAttribute('data-line');
    var qty = parseInt(btn.getAttribute('data-cart-change'), 10);
    var drawer = document.getElementById('ef-cart-drawer');
    if (drawer) drawer.classList.add('is-busy');

    fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: parseInt(line, 10), quantity: qty })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('ef:cart:update', { detail: cart }));
        return refreshCart();
      })
      .finally(function () { if (drawer) drawer.classList.remove('is-busy'); });
  });

  /* Anything that adds to cart dispatches this; the drawer opens rather than
     navigating away, so the customer keeps their place on the page. */
  document.addEventListener('ef:cart:added', function () {
    refreshCart().then(function () {
      var drawer = document.getElementById('ef-cart-drawer');
      if (drawer) open(drawer);
    });
  });

  /* Product forms submit through here so they open the drawer too. */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[action*="/cart/add"]');
    if (!form || form.hasAttribute('data-no-drawer')) return;
    if (!document.getElementById('ef-cart-drawer')) return;
    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: new FormData(form)
    })
      .then(function (r) { if (!r.ok) throw r; return r.json(); })
      .then(function () { return fetch(window.Shopify.routes.root + 'cart.js').then(function (r) { return r.json(); }); })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('ef:cart:update', { detail: cart }));
        document.dispatchEvent(new CustomEvent('ef:cart:added'));
      })
      .catch(function () { form.setAttribute('data-no-drawer', ''); form.submit(); })
      .finally(function () { if (btn) { btn.disabled = false; btn.textContent = label; } });
  });

  window.efDrawer = { open: open, close: close, refreshCart: refreshCart };
})();
